import React, { memo, useCallback, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Button,
  ButtonGroup,
  Icon,
  Menu,
  MenuItem,
  MenuList,
  MenuButton,
  IconButton,
  Text,
  Textarea,
  useDisclosure,
} from "@chakra-ui/react";
import { useSelector, useDispatch } from "react-redux";
import {
  FaBan,
  FaEllipsisV,
  FaCheckDouble,
  FaChevronDown,
  FaPen,
  FaLink,
  FaReply,
  FaTrash,
  FaHandPaper,
} from "react-icons/fa";
import { analyticsTrackEvent } from "../../../_utils/common";
import { updateDraftStatus } from "../../../_actions/plannerActions";
import { addComment } from "../../../_actions/commentActions";
import ConfirmModal from "../../../_components/confirmModal/ConfirmModal";
import ExternalApprovalModal from "../../../_components/externalApprovalModal/ExternalApprovalModal";
import DraftStatuses from "../../../_constants/DraftStatuses";

const DraftMenu = ({
  buttonProps,
  labelVisible,
  onChangeStatus,
  onDelete,
  onEdit,
  onUse,
  variant,
  draft,
  ml,
  mr,
  ...rest
}) => {
  const { t } = useTranslation("planner");
  const dispatch = useDispatch();
  const {
    isOpen: isRejectOpen,
    onOpen: onRejectOpen,
    onClose: onRejectClose,
  } = useDisclosure();
  const {
    isOpen: isExternalApprovalModalOpen,
    onOpen: onExternalApprovalModalOpen,
    onClose: onExternalApprovalModalClose,
  } = useDisclosure();
  const [comment, setComment] = useState();
  const [loading, setLoading] = useState(false);
  const permissions = useSelector(
    (state) => state.permissions.workspacePermissionChecks
  );
  const userId = useSelector((state) => state.auth.payload?.user?.pk);
  const {
    canApproveDrafts,
    canEditDrafts,
    canEditDraftsAll,
    canEditPosts,
    canEditPostsAll,
  } = permissions;

  const handleApprove = useCallback(() => {
    dispatch(
      updateDraftStatus({
        status: "accept",
        draftIds: [draft.post.id],
      })
    );

    analyticsTrackEvent("draftApproved");
    onChangeStatus();
  }, [draft, dispatch]);

  const handleReject = useCallback(() => {
    onRejectOpen();
  }, [onRejectOpen]);

  const handleRejectConfirm = useCallback(async () => {
    setLoading(true);

    if (comment) {
      try {
        await addComment({
          comment,
          content_type: "post",
          object_pk: draft.post.id,
          user: userId,
        });
      } catch {
        setLoading(false);
      }
    }

    onRejectClose();
    dispatch(
      updateDraftStatus({
        status: "reject",
        draftIds: [draft.post.id],
      })
    );

    analyticsTrackEvent("draftRejected");
    onChangeStatus();
    setLoading(false);
  }, [draft, dispatch, onChangeStatus, setLoading, comment]);

  const handleRenew = useCallback(() => {
    dispatch(
      updateDraftStatus({
        status: "renew",
        draftIds: [draft.post.id],
      })
    );

    analyticsTrackEvent("draftRenewed");
    onChangeStatus();
  }, [draft, dispatch, onChangeStatus]);

  const handleRequestApproval = useCallback(() => {
    dispatch(
      updateDraftStatus({
        status: "complete",
        draftIds: [draft.post.id],
      })
    );

    analyticsTrackEvent("draftRequestedApproval");

    onChangeStatus();
  }, [draft, dispatch, onChangeStatus]);

  const handleExternalApprovalModalClose = useCallback(() => {
    onExternalApprovalModalClose();
    onChangeStatus();
  }, [onChangeStatus, onExternalApprovalModalClose]);

  const renderMainLabel = () => {
    if (
      [DraftStatuses.NEW, DraftStatuses.REJECTED, DraftStatuses.REJECTED_EXTERNAL].includes(draft?.info?.status?.type)
    ) {
      return (
        <Button
          onClick={handleRequestApproval}
          leftIcon={<Icon as={FaHandPaper} />}
        >
          {t("drafts.request_approval")}
        </Button>
      );
    }
    if (canApproveDrafts && draft?.info?.status?.type === DraftStatuses.DONE) {
      return (
        <Button onClick={handleApprove} leftIcon={<Icon as={FaCheckDouble} />}>
          {t("drafts.approve")}
        </Button>
      );
    }
    if (canEditPostsAll || (canEditPosts && draft.info.isMine)) {
      return (
        <Button onClick={onUse} leftIcon={<Icon as={FaReply} />}>
          {t("drafts.schedule")}
        </Button>
      );
    }

    return null;
  };

  if (
    !canApproveDrafts &&
    !canEditDraftsAll &&
    !draft.info.isMine &&
    draft?.info?.status?.type !== DraftStatuses.REJECTED &&
    draft?.info?.status?.type !== DraftStatuses.REJECTED_EXTERNAL
  ) {
    // case where user doesn't have any rights to manage someone elses drafts
    // and this is not his draft - don't show menu at all;
    return null;
  }

  return (
    <ButtonGroup
      size="sm"
      isAttached={labelVisible}
      variant={variant}
      ml={ml}
      mr={mr}
    >
      {labelVisible && renderMainLabel()}
      <Menu isLazy minWidth="6" autoSelect={false} {...rest}>
        <MenuButton
          variant={variant}
          as={IconButton}
          icon={labelVisible ? <FaChevronDown /> : <FaEllipsisV />}
          aria-label="Options"
          onClick={(e) => e.stopPropagation()}
          borderLeft={labelVisible ? "1px solid" : "none"}
          borderLeftColor={
            variant === "solid" ? "whiteAlpha.400" : "blackAlpha.400"
          }
          {...buttonProps}
        />
        <MenuList
          w="160px"
          minW="160px"
          fontSize="xs"
          onClick={(e) => e.stopPropagation()}
        >
          {([DraftStatuses.NEW, DraftStatuses.REJECTED, DraftStatuses.REJECTED_EXTERNAL].includes(draft?.info?.status?.type)) && (
            <MenuItem onClick={handleRequestApproval}>
              <Icon as={FaHandPaper} mr="3" />
              {t("drafts.request_approval")}
            </MenuItem>
          )}
          {([DraftStatuses.NEW, DraftStatuses.REJECTED, DraftStatuses.REJECTED_EXTERNAL].includes(draft?.info?.status?.type)) && (
            <MenuItem onClick={onExternalApprovalModalOpen}>
              <Icon as={FaLink} mr="3" />
              {t("drafts.external_approval")}
            </MenuItem>
          )}
          {canApproveDrafts &&
            draft?.info?.status?.type === DraftStatuses.DONE && (
              <MenuItem onClick={handleApprove}>
                <Icon as={FaCheckDouble} mr="3" />
                {t("drafts.approve")}
              </MenuItem>
            )}
          {canApproveDrafts &&
            draft?.info?.status?.type === DraftStatuses.DONE && (
              <MenuItem onClick={handleReject}>
                <Icon as={FaBan} mr="3" />
                {t("drafts.reject")}
              </MenuItem>
            )}
          {(canEditPostsAll || (canEditPosts && draft.info.isMine)) && (
            <MenuItem onClick={onUse}>
              <Icon as={FaReply} mr="3" />
              {t("drafts.schedule")}
            </MenuItem>
          )}
          {(canEditDraftsAll || (canEditDrafts && draft.info.isMine)) && (
            <MenuItem onClick={onEdit}>
              <Icon as={FaPen} mr="3" />
              {t("posts.edit")}
            </MenuItem>
          )}
          {([DraftStatuses.DONE, DraftStatuses.ACCEPTED, DraftStatuses.ACCEPTED_EXTERNAL].includes(draft?.info?.status?.type)) &&
            (canEditDraftsAll || (canEditDrafts && draft.info.isMine)) && (
              <MenuItem onClick={handleRenew}>
                <Icon as={FaReply} mr="3" />
                {t("drafts.send_back_to_drafts")}
              </MenuItem>
            )}
          {(canEditDraftsAll || (canEditDrafts && draft.info.isMine)) && (
            <MenuItem onClick={onDelete}>
              <Icon as={FaTrash} mr="3" />
              {t("posts.delete")}
            </MenuItem>
          )}
        </MenuList>
      </Menu>
      <ConfirmModal
        isOpen={isRejectOpen}
        title={t("drafts.reject_draft")}
        confirmButtonText={t("drafts.reject_draft")}
        cancelButtonText={t("cancel", { ns: "common" })}
        loading={loading}
        onConfirm={handleRejectConfirm}
        onClose={onRejectClose}
      >
        <Text color="gray.200" fontSize="sm" mb="4">
          {t("drafts.reject_tip")}
        </Text>
        <Textarea
          disabled={loading}
          fontSize="sm"
          onChange={(e) => setComment(e.target.value)}
          p="3"
          placeholder={t("comments.add_optional", { ns: "common" })}
          resize="none"
          value={comment}
        />
      </ConfirmModal>
      {isExternalApprovalModalOpen && (
        <ExternalApprovalModal
          draftIds={[draft.post.id]}
          typeName={t("post", { count: 1 })}
          onClose={handleExternalApprovalModalClose}
        />
      )}
    </ButtonGroup>
  );
};

DraftMenu.propTypes = {
  buttonProps: PropTypes.object,
  labelVisible: PropTypes.bool,
  ml: PropTypes.string,
  mr: PropTypes.string,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  onUse: PropTypes.func,
  onChangeStatus: PropTypes.func,
  variant: PropTypes.string,
};

DraftMenu.defaultProps = {
  buttonProps: undefined,
  labelVisible: false,
  ml: "0",
  mr: "0",
  onDelete: undefined,
  onEdit: undefined,
  onUse: undefined,
  onChangeStatus: () => {},
  variant: "solid",
};

export default memo(DraftMenu);
